import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';

/**
 * Export data to CSV format
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name of the file (without extension)
 */
export const exportToCSV = (data, filename = 'export') => {
  try {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    const excelBuffer = XLSX.write(workbook, { bookType: 'csv', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `${filename}_${Date.now()}.csv`);
    return { success: true, message: 'CSV exported successfully' };
  } catch (error) {
    console.error('CSV Export Error:', error);
    return { success: false, message: 'Failed to export CSV' };
  }
};

/**
 * Export chart/element to PNG format
 * @param {HTMLElement|string} element - HTML element or selector to capture
 * @param {string} filename - Name of the file (without extension)
 */
export const exportToPNG = async (element, filename = 'chart') => {
  try {
    const targetElement = typeof element === 'string' 
      ? document.querySelector(element) 
      : element;

    if (!targetElement) {
      throw new Error('Element not found');
    }

    const canvas = await html2canvas(targetElement, {
      backgroundColor: '#ffffff',
      scale: 2,
      logging: false,
      useCORS: true
    });

    canvas.toBlob((blob) => {
      saveAs(blob, `${filename}_${Date.now()}.png`);
    });

    return { success: true, message: 'PNG exported successfully' };
  } catch (error) {
    console.error('PNG Export Error:', error);
    return { success: false, message: 'Failed to export PNG' };
  }
};

/**
 * Export chart/element to PDF format using browser print dialog
 * @param {HTMLElement|string} element - HTML element or selector to capture
 * @param {string} filename - Name of the file (without extension)
 * @param {string} title - Title for the PDF
 */
export const exportToPDF = async (element, filename = 'report', title = 'Report') => {
  try {
    const targetElement = typeof element === 'string' 
      ? document.querySelector(element) 
      : element;

    if (!targetElement) {
      throw new Error('Element not found');
    }

    const canvas = await html2canvas(targetElement, {
      backgroundColor: '#ffffff',
      scale: 2,
      logging: false,
      useCORS: true
    });

    // Open print dialog for true PDF
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>${title}</title>
            <style>
              body { margin: 0; padding: 20px; text-align: center; }
              h1 { font-family: Arial, sans-serif; margin-bottom: 20px; }
              img { max-width: 100%; height: auto; }
              @media print {
                body { padding: 0; }
                h1 { page-break-after: avoid; }
              }
            </style>
          </head>
          <body>
            <h1>${title}</h1>
            <img src="${canvas.toDataURL()}" />
            <script>
              window.onload = () => {
                window.print();
                setTimeout(() => window.close(), 100);
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
      return { success: true, message: 'PDF export initiated (use browser Print to PDF)' };
    } else {
      throw new Error('Pop-up blocked. Please allow pop-ups for this site.');
    }
  } catch (error) {
    console.error('PDF Export Error:', error);
    return { success: false, message: 'Failed to export PDF' };
  }
};

/**
 * Export chart/element as image for PPT
 * @param {HTMLElement|string} element - HTML element or selector to capture
 * @param {string} filename - Name of the file (without extension)
 */
export const exportToPPT = async (element, filename = 'presentation') => {
  try {
    const result = await exportToPNG(element, filename);
    return { 
      success: result.success, 
      message: result.success ? 'Image exported (can be inserted into PowerPoint)' : result.message 
    };
  } catch (error) {
    console.error('PPT Export Error:', error);
    return { success: false, message: 'Failed to export for PPT' };
  }
};

/**
 * Export multiple charts via print dialog
 * @param {Array} elements - Array of { element, title } objects
 * @param {string} filename - Name of the file (without extension)
 */
export const exportMultipleToPDF = async (elements, filename = 'report') => {
  try {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      throw new Error('Pop-up blocked. Please allow pop-ups.');
    }

    let htmlContent = `
      <html>
        <head>
          <title>Multi-Chart Report</title>
          <style>
            body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
            .page { page-break-after: always; margin-bottom: 40px; }
            .page:last-child { page-break-after: auto; }
            h2 { margin-bottom: 20px; text-align: center; }
            img { max-width: 100%; height: auto; display: block; margin: 0 auto; }
            @media print {
              body { padding: 0; }
              .page { page-break-after: always; }
            }
          </style>
        </head>
        <body>
    `;

    for (let i = 0; i < elements.length; i++) {
      const { element, title } = elements[i];
      const targetElement = typeof element === 'string' 
        ? document.querySelector(element) 
        : element;

      if (!targetElement) continue;

      const canvas = await html2canvas(targetElement, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true
      });

      htmlContent += `
        <div class="page">
          <h2>${title || `Chart ${i + 1}`}</h2>
          <img src="${canvas.toDataURL()}" />
        </div>
      `;
    }

    htmlContent += `
          <script>
            window.onload = () => {
              window.print();
              setTimeout(() => window.close(), 100);
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();

    return { success: true, message: 'Multi-page PDF export initiated' };
  } catch (error) {
    console.error('Multi-page PDF Export Error:', error);
    return { success: false, message: 'Failed to export multi-page PDF' };
  }
};

/**
 * Export multiple charts as PNG images
 * @param {Array} elements - Array of { element, title } objects
 * @param {string} filename - Name of the file (without extension)
 */
export const exportMultipleToPPT = async (elements, filename = 'presentation') => {
  try {
    for (let i = 0; i < elements.length; i++) {
      const { element } = elements[i];
      if (element) {
        await exportToPNG(element, `${filename}_slide_${i + 1}`);
      }
    }
    return { success: true, message: 'Images exported (can be inserted into PowerPoint)' };
  } catch (error) {
    console.error('Multi-slide PPT Export Error:', error);
    return { success: false, message: 'Failed to export for PPT' };
  }
};
